import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';
import { CONFIG } from '../config';
import { buildUrl } from '../utils';
import { ApplianceDebuggerApi } from './ApplianceDebuggerApi';

import {
  ResponseError,
  UserAppliancesResponse,
  RegisterApplianceResponse,
  UserCredentials,
  RegisterRequest,
  SendCommandRequest,
  ApplianceCapabilitiesWithValuesResponse,
  ApplianceStateResponse,
  SendCommandResponse,
  FirmwareApplianceResponse,
  UpdateFirmwareRequest,
  UpdateFirmwareResponse,
} from './types';

/** @public */
export class ApplianceDebuggerClient implements ApplianceDebuggerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  async postUserAppliances(
    credentials: UserCredentials,
  ): Promise<UserAppliancesResponse> {
    const response = await this.post(
      buildUrl(CONFIG.baseUrl, {
        path: `/registration/auth-appliances`,
      }),
      credentials,
    );
    return response;
  }

  async registerAppliance(
    request: RegisterRequest,
    applianceId: string,
  ): Promise<RegisterApplianceResponse> {
    const response = await this.post(
      buildUrl(CONFIG.baseUrl, {
        path: `/registration/${applianceId}`,
      }),
      request,
    );
    return response;
  }

  async get(path: string): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();
    const response = await fetch(url, {
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    });

    if (!response.ok) {
      const body = response;
      const message = `Request failed with ${response.status} ${response.statusText}, ${body}`;
      throw new ResponseError(message, response);
    }

    const body = await response.json();
    return { status: response.status, body };
  }

  async post(path: string, payload: { [key: string]: any }): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const body = response;
      const message = `Request failed with ${response.status} ${response.statusText}, ${body}`;
      throw new ResponseError(message, response);
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    let body = {};
    if (isJson) {
      const text = await response.text();
      try {
        body = text ? JSON.parse(text) : {};
      } catch (error) {
        body = {};
      }
    }

    return { status: response.status, body };
  }

  async delete(path: string, payload: { [key: string]: any }): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      method: 'DELETE',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const body = response;
      const message = `Request failed with ${response.status} ${response.statusText}, ${body}`;
      throw new ResponseError(message, response);
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    let body = {};
    if (isJson) {
      const text = await response.text();
      try {
        body = text ? JSON.parse(text) : {};
      } catch (error) {
        body = {};
      }
    }

    return { status: response.status, body };
  }
  async getApplianceCapabilitiesWithValues(
    applianceId: string,
  ): Promise<ApplianceCapabilitiesWithValuesResponse> {
    const url = buildUrl(CONFIG.baseUrl, {
      path: `/debugger/${applianceId}/capabilities/properties-with-values`,
    });
    return await this.get(url);
  }
  async getFirmwareByApplianceId(
    applianceId: string,
    cdcId: string,
  ): Promise<FirmwareApplianceResponse> {
    const url = buildUrl(CONFIG.baseUrl, {
      path: `/debugger/appliances/${applianceId}/firmwares`,
      queryParams: { cdcId },
    });
    return await this.get(url);
  }
  async getApplianceState(
    applianceId: string,
  ): Promise<ApplianceStateResponse> {
    const url = buildUrl(CONFIG.baseUrl, {
      path: `/appliances/${applianceId}/all-shadows`,
    });
    return await this.get(url);
  }

  async sendCommand(
    applianceId: string,
    request: SendCommandRequest,
    handler?: string,
  ): Promise<SendCommandResponse> {
    const url = buildUrl(CONFIG.baseUrl, {
      path: `/debugger/${applianceId}/sendCommand`,
      queryParams: handler ? { handler } : undefined,
    });
    return await this.put(url, request);
  }
  async updateFirmware(
    applianceId: string,
    request: UpdateFirmwareRequest,
  ): Promise<UpdateFirmwareResponse> {
    const url = buildUrl(CONFIG.baseUrl, {
      path: `/debugger/appliances/${applianceId}/desired-state`,
    });
    return await this.patch(url, request);
  }

  async put(path: string, payload: { [key: string]: any }): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const body = response;
      const message = `Request failed with ${response.status} ${response.statusText}, ${body}`;
      throw new ResponseError(message, response);
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    let body = {};
    if (isJson) {
      const text = await response.text();
      try {
        body = text ? JSON.parse(text) : {};
      } catch (error) {
        body = {};
      }
    }

    return { status: response.status, body };
  }
  async patch(path: string, payload: { [key: string]: any }): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const body = response;
      const message = `Request failed with ${response.status} ${response.statusText}, ${body}`;
      throw new ResponseError(message, response);
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    let body = {};
    if (isJson) {
      const text = await response.text();
      try {
        body = text ? JSON.parse(text) : {};
      } catch (error) {
        body = {};
      }
    }

    return { status: response.status, body };
  }
}
